import { useState } from 'react';
import { Link, Navigate, useNavigate } from '@tanstack/react-router';
import { temFicha, type ItemResponse } from '@triade/contracts';
import { useCriarFicha, useFichasDoItem } from '@/api/fichas';
import { usePode } from '@/api/me';
import { useItem } from '@/api/itens';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { ApiError } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { FichasListaPage } from './fichas-lista-page';
import type { BuscaFicha } from './tipos';

/**
 * Entrada `/custos/fichas-tecnicas`: com `itemId`, abre a versao escolhida do item (senao a mais nova; item sem ficha oferece criar a
 * primeira). Sem `itemId`, mostra a lista de fichas (uma linha por item) com a busca da URL.
 */
export function FichaItemPage({ itemId, busca = {} }: { itemId?: string; busca?: BuscaFicha }) {
  const { empresaId } = useSessao();
  let corpo;
  if (empresaId === null) corpo = <Aviso>Escolha a empresa no menu lateral para operar.</Aviso>;
  else if (!itemId) corpo = <FichasListaPage busca={busca} />;
  else corpo = <ResolverVersao itemId={itemId} />;
  return (
    <div className="flex w-full flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Custos <span aria-hidden="true">/</span> Fichas técnicas
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">{itemId ? 'Ficha técnica' : 'Fichas técnicas'}</h1>
        </div>
      </div>
      {corpo}
    </div>
  );
}

function ResolverVersao({ itemId }: { itemId: string }) {
  const navigate = useNavigate();
  const item = useItem(itemId);
  const versoes = useFichasDoItem(itemId);
  const criar = useCriarFicha();
  const podeCriar = usePode().pode('ficha.editar');
  const [erro, setErro] = useState<string | null>(null);

  if (item.isError) return <ErroFormacao titulo="Não foi possível carregar o item" erro={item.error} onTentar={() => void item.refetch()} />;
  // Tipo sem ficha (insumo, mercadoria): só aviso, sem criar versão e sem redirecionar para versões antigas.
  if (item.data && !temFicha(item.data.tipo)) return <ItemCustoAquisicao item={item.data} />;
  if (versoes.isError) return <ErroFormacao titulo="Não foi possível carregar as versões" erro={versoes.error} onTentar={() => void versoes.refetch()} />;
  if (!versoes.data || !item.data) return <Aviso papel="status">Carregando ficha técnica…</Aviso>;

  const alvo = versoes.data.find((v) => v.escolhida) ?? versoes.data[0];
  if (alvo) return <Navigate to="/custos/fichas-tecnicas/$id" params={{ id: alvo.id }} replace />;

  return <SemFicha item={item.data} erro={erro} criando={criar.isPending} podeCriar={podeCriar} onCriar={async () => {
    setErro(null);
    try {
      const f = await criar.mutateAsync({ itemId });
      void navigate({ to: '/custos/fichas-tecnicas/$id', params: { id: f.id }, replace: true });
    } catch (e) {
      setErro(e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');
    }
  }} />;
}

function ItemCustoAquisicao({ item }: { item: ItemResponse }) {
  return (
    <section aria-label="Item com custo de aquisição" className="rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center">
      <p className="font-semibold">
        {item.codigo} · {item.descricao}
      </p>
      <p className="mt-1 text-small text-ink-muted">Este tipo usa custo de aquisição.</p>
      <Link to="/cadastros/itens/$id" params={{ id: item.id }} className="mt-4 inline-block text-small font-medium text-primary hover:underline">
        Abrir o cadastro do item
      </Link>
    </section>
  );
}

function SemFicha({ item, erro, criando, onCriar, podeCriar }: { item: ItemResponse; erro: string | null; criando: boolean; onCriar: () => void; podeCriar: boolean }) {
  return (
    <section aria-label="Item sem ficha" className="rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center">
      <p className="font-semibold">
        {item.codigo} · {item.descricao}
      </p>
      <p className="mt-1 text-small text-ink-muted">Este item ainda não tem ficha técnica.</p>
      {erro && (
        <p role="alert" className="mx-auto mt-3 max-w-lg rounded-xl border border-destructive/30 bg-danger-bg px-4 py-2 text-small text-danger-ink">
          {erro}
        </p>
      )}
      {podeCriar && (
        <button
          type="button"
          onClick={onCriar}
          disabled={criando}
          className="mt-4 h-9 rounded-lg bg-primary px-4 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {criando ? 'Criando…' : 'Criar a primeira versão'}
        </button>
      )}
    </section>
  );
}

function Aviso({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}

